import React, { Component } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import StorageHandler from 'config/StorageHandler';
import { storageKeys } from 'config/settings.json';
import i18n from 'i18n-react';
import { toast, Zoom } from 'react-toastify';

export default class ArrangeTheWords extends Component {
  constructor(props) {
    super(props);
    const { words, completedBefore } = props;

    this.state = {
      blanks: completedBefore ? [...words] : Array(words.length).fill(null),
      availableWords: completedBefore ? [] : this.shuffleArray([...words]),
      answerStyle: !!completedBefore,
      isIncorrect: false,
      headerImage: '',
    };

    this.handleWordSelect = this.handleWordSelect.bind(this);
    this.handleRemoveWord = this.handleRemoveWord.bind(this);
  }

  componentDidMount() {
    this.setHeader();
    if (this.props.completedBefore) {
      this.props.contentComplete();
    }
  }

  componentDidUpdate(prevProps) {
    const { quizMode, quizTimerExpired, error } = this.props;
    if (quizMode && quizTimerExpired && !prevProps.quizTimerExpired) {
      setTimeout(() => {
        return error(true);
      }, 600);
    }
    if (!prevProps.completedBefore && this.props.completedBefore) {
      const { words, contentComplete } = this.props;
      this.setState({
        blanks: [...words],
        availableWords: [],
        answerStyle: true,
      });
      contentComplete();
    }
  }

  async setHeader() {
    const appInfo = await StorageHandler.load(storageKeys.appInfo);
    const { questionHeader } = appInfo;
    this.setState({ headerImage: questionHeader });
  }

  shuffleArray(array) {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  isAnswerCorrect(blanks, words) {
    return blanks.every((word, i) => {
      return word?.toLowerCase() === words[i]?.toLowerCase();
    });
  }

  checkAnswer() {
    if (this.props.completedBefore) return;
    const { blanks } = this.state;
    const { words, contentComplete, incorrectAnswerText, error } = this.props;

    const isCorrect = this.isAnswerCorrect(blanks, words);

    if (isCorrect) {
      this.setState({ answerStyle: true, isIncorrect: false });
      contentComplete();
    } else {
      toast.error(incorrectAnswerText, {
        position: 'top-center',
        transition: Zoom,
      });
      error(true);
      this.setState({ answerStyle: false, isIncorrect: true });
    }
  }

  handleWordSelect(word) {
    if (this.props.completedBefore) return;
    this.setState((prevState) => {
      const nextBlankIndex = prevState.blanks.findIndex((b) => {
        return b === null;
      });
      if (nextBlankIndex === -1) return null;

      const newBlanks = [...prevState.blanks];
      newBlanks[nextBlankIndex] = word;

      const newAvailableWords = prevState.availableWords.filter((w) => {
        return w !== word;
      });

      return {
        blanks: newBlanks,
        availableWords: newAvailableWords,
      };
    });
  }

  handleRemoveWord(index) {
    if (this.props.completedBefore) return;
    this.setState((prevState) => {
      const wordToReturn = prevState.blanks[index];
      if (!wordToReturn) return null;

      const newBlanks = [...prevState.blanks];
      newBlanks[index] = null;

      return {
        blanks: newBlanks,
        availableWords: [...prevState.availableWords, wordToReturn],
      };
    });
  }

  render() {
    const { question, disableHeader, quizMode } = this.props;
    const { blanks, availableWords, headerImage } = this.state;

    const questionParts = question.split(/\[blank\]/g);

    const completedBefore = this.props.completedBefore;

    return (
      <div className={clsx('arrangeWords__container', 'container__question', { 'quiz-mode-card': quizMode })}>
        {!disableHeader && (
          <div className="container__question--image" style={{ backgroundImage: `url(${headerImage})` }} />
        )}

        <p className="arrangeWords__description">{i18n.translate('questions.arrangeTheWords')}</p>

        <div className="arrangeWords__sentence">
          <h4>
            {questionParts.map((part, i) => {
              return (
                <span key={i}>
                  {part}
                  {i < blanks.length &&
                    (blanks[i] !== null ? (
                      <span
                        onClick={
                          completedBefore
                            ? undefined
                            : () => {
                                return this.handleRemoveWord(i);
                              }
                        }
                        className={clsx('arrangeWords__filledBlank', {
                          correct: this.state.answerStyle,
                          incorrect: this.state.isIncorrect,
                        })}
                      >
                        {blanks[i]}
                      </span>
                    ) : (
                      <span className="arrangeWords__blank">{'_____'}</span>
                    ))}
                </span>
              );
            })}
          </h4>
        </div>

        {!completedBefore && (
          <div className="arrangeWords__availableWords">
            {availableWords.map((word, i) => {
              return (
                <button
                  key={`${word}-${i}`}
                  onClick={() => {
                    return this.handleWordSelect(word);
                  }}
                  className="arrangeWords__availableWordButton"
                >
                  {word}
                </button>
              );
            })}
          </div>
        )}

        {!completedBefore && (
          <button
            onClick={() => {
              this.checkAnswer();
            }}
            style={this.state.answerStyle ? { backgroundColor: '#6cd488' } : null}
          >
            {'Submit'}
          </button>
        )}
        {this.props.correct(false)}
        {this.props.error(false)}
      </div>
    );
  }
}

ArrangeTheWords.propTypes = {
  words: PropTypes.arrayOf(PropTypes.string).isRequired,
  question: PropTypes.string.isRequired,
  correct: PropTypes.func,
  correctAnswerText: PropTypes.string,
  incorrectAnswerText: PropTypes.string,
  contentComplete: PropTypes.func,
  completedBefore: PropTypes.bool,
  disableHeader: PropTypes.bool,
  error: PropTypes.func,
  title: PropTypes.string,
  quizMode: PropTypes.bool,
  quizTimerExpired: PropTypes.bool,
};

ArrangeTheWords.defaultProps = {
  correct: () => {
    return undefined;
  },
  correctAnswerText: 'Correct!',
  incorrectAnswerText: 'Try again',
  contentComplete: () => {
    return undefined;
  },
  completedBefore: false,
  disableHeader: false,
  error: () => {
    return undefined;
  },
  title: null,
  quizMode: false,
  quizTimerExpired: false,
};
